/**
 * Exbabel - Frontend Application
 * Copyright (c) 2025 Exbabel. All Rights Reserved.
 * 
 * Auth + Membership UX:
 * - Visitors can join sessions without login
 * - Members can use Solo mode
 * - Admins can host sessions
 */

import React, { lazy, Suspense, useState, useEffect, useRef } from 'react'
import { ExbabelLoading } from './components/ui/ExbabelLoading'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { getPendingCheckoutUrl, getActiveCheckoutUrl } from '@/lib/pendingCheckout'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { LoginPage } from '@/components/auth/LoginPage'
import { SignUpPage } from '@/components/auth/SignUpPage'
import { ForgotPasswordPage } from '@/components/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/components/auth/ResetPasswordPage'
const JoinPage = lazy(() => import('@/components/JoinPage').then(m => ({ default: m.JoinPage })))
const JoinChurchPage = lazy(() => import('@/components/JoinChurchPage').then(m => ({ default: m.JoinChurchPage })))
const CreateChurchPage = lazy(() => import('@/components/CreateChurchPage').then(m => ({ default: m.CreateChurchPage })))
const OnboardingPage = lazy(() => import('@/components/OnboardingPage'))
import { VisitorHome } from '@/components/home/VisitorHome'
const MemberHome = lazy(() => import('@/components/home/MemberHome').then(m => ({ default: m.MemberHome })))
const AdminHome = lazy(() => import('@/components/home/AdminHome').then(m => ({ default: m.AdminHome })))
const WorkspaceHub = lazy(() => import('@/components/home/WorkspaceHub').then(m => ({ default: m.WorkspaceHub })))
const SoloPage = lazy(() => import('./components/solo').then(m => ({ default: m.SoloPage })))
const HostPage = lazy(() => import('./components/HostPage').then(m => ({ default: m.HostPage })))
const ListenerPage = lazy(() => import('./components/ListenerPage').then(m => ({ default: m.ListenerPage })))
const LivestreamPlayerPage = lazy(() => import('./components/LivestreamPlayerPage').then(m => ({ default: m.LivestreamPlayerPage })))
const LivestreamDashboard = lazy(() => import('./components/admin/LivestreamDashboard').then(m => ({ default: m.LivestreamDashboard })))
const LivestreamAnalytics = lazy(() => import('./components/admin/analytics/LivestreamAnalytics').then(m => ({ default: m.default || m.LivestreamAnalytics })))
const DemoPage = lazy(() => import('./components/DemoPage'))
const SalesDemoPage = lazy(() => import('./components/SalesDemoPage'))
const BillingPage = lazy(() => import('./components/BillingPage').then(m => ({ default: m.BillingPage })))
const TranslateCheckout = lazy(() => import('./components/TranslateCheckout').then(m => ({ default: m.TranslateCheckout })))
const MissionaryOnboarding = lazy(() => import('./components/MissionaryOnboarding').then(m => ({ default: m.MissionaryOnboarding })))
const MissionaryTracking = lazy(() => import('./components/MissionaryTracking').then(m => ({ default: m.MissionaryTracking })))
const LiveCheckout = lazy(() => import('./components/LiveCheckout').then(m => ({ default: m.LiveCheckout })))
const CustomLiveSignup = lazy(() => import('./components/CustomLiveSignup').then(m => ({ default: m.CustomLiveSignup })))
const EventsCheckout = lazy(() => import('./components/events/EventsCheckout').then(m => ({ default: m.EventsCheckout })))
const GlobalSettingsPage = lazy(() => import('./components/home/GlobalSettingsPage').then(m => ({ default: m.GlobalSettingsPage })))
const VoicesPage = lazy(() => import('./components/VoicesPage'))
const EmployeePortal = lazy(() => import('./components/employee/EmployeePortal').then(m => ({ default: m.EmployeePortal })))
const AdminEmployeeManagement = lazy(() => import('./components/admin/AdminEmployeeManagement').then(m => ({ default: m.AdminEmployeeManagement })))
const EmployeeAuth = lazy(() => import('./components/employee/EmployeeAuth').then(m => ({ default: m.EmployeeAuth })))
const IntelligenceHubHome = lazy(() => import('./components/intelligence/IntelligenceHubHome').then(m => ({ default: m.IntelligenceHubHome })))
const SermonIntelligenceDetail = lazy(() => import('./components/intelligence/SermonIntelligenceDetail').then(m => ({ default: m.SermonIntelligenceDetail })))
const PublicSermonIntelligenceDetail = lazy(() => import('./components/intelligence/PublicSermonIntelligenceDetail').then(m => ({ default: m.PublicSermonIntelligenceDetail })))
const ListenerSermonLibrary = lazy(() => import('./components/intelligence/ListenerSermonLibrary').then(m => ({ default: m.ListenerSermonLibrary })))
const PersonalizedDemoPage = lazy(() => import('./components/demos/PersonalizedDemoPage'))
const AdminDemosPage = lazy(() => import('./components/demos/AdminDemosPage').then(m => ({ default: m.AdminDemosPage })))

// Exbabel Events 2.0 components
import { OrgProvider } from '@/contexts/OrgContext'
import { eventHostBackDestination, eventHostUrl } from '@/components/events/eventHostNavigation'
const EventsDashboard = lazy(() => import('./components/events/EventsDashboardV2').then(m => ({ default: m.EventsDashboard })))
const MyEvents = lazy(() => import('./components/events/MyEventsV2').then(m => ({ default: m.MyEvents })))
const EventDetail = lazy(() => import('./components/events/EventDetail').then(m => ({ default: m.EventDetail })))
const SessionDetail = lazy(() => import('./components/events/SessionDetail').then(m => ({ default: m.SessionDetail })))
const EventsBilling = lazy(() => import('./components/events/EventsBilling').then(m => ({ default: m.EventsBilling })))
const OperatorLinkEntry = lazy(() => import('./components/events/OperatorLinkEntry').then(m => ({ default: m.OperatorLinkEntry })))
const PurityEventsWrapper = lazy(() => import('./components/events/PurityEventsWrapper').then(m => ({ default: m.PurityEventsWrapper })))
const EventsAnalyticsPage = lazy(() => import('./components/events/EventsAnalyticsPage').then(m => ({ default: m.EventsAnalyticsPage })))
const IntegrationsPage = lazy(() => import('./components/events/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })))
const ZoomInterpretationApp = lazy(() => import('./components/events/ZoomInterpretationApp').then(m => ({ default: m.ZoomInterpretationApp })))

// Protected route wrapper for routes that require authentication
function ProtectedRoute({ children, requireAuth = true, requireMember = false, requireAdmin = false }) {
  const { isAuthenticated, isMember, isAdmin, loading, error } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
        <ExbabelLoading label="Loading account" />
      </div>
    )
  }

  if (error === 'network_error' || error === 'api_error') {
    return (
      <div className="min-h-screen flex flex-col gap-4 items-center justify-center p-6 text-center">
        <p>We could not load your account. Check your connection and try again.</p>
        <button className="rounded bg-blue-600 px-5 py-2 text-white" onClick={() => window.location.reload()}>Try again</button>
      </div>
    )
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/signin" replace />
  }

  if (requireMember && !isMember && !isAdmin) {
    return <Navigate to="/" replace />
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />
  }

  return children
}

// Protected route wrapper specific to the Employee Portal
function ProtectedEmployeeRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <ExbabelLoading label="Loading account" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/employeeportal/login" replace />;
  }
  
  // Note: True employee verification happens in the backend via requireEmployee middleware
  return children;
}

// Home route that redirects based on user state
function HomeRoute() {
  const { user, profile, loading, error, isAuthenticated, isVisitor, isMember, isAdmin, hasChurch, signOut, access } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  // Handle Supabase email verification redirects
  useEffect(() => {
    // Supabase redirects to the site default URL with a hash containing access_token
    // If the original signup had a redirect URL (like ?redirect=/checkout), we need to extract that
    const hash = window.location.hash;
    const pathParams = searchParams.get('redirect');

    if (hash && hash.includes('access_token')) {
      // Invitation or Password Recovery -> Set Password
      if (hash.includes('type=recovery') || hash.includes('type=invite')) {
        navigate('/set-password' + window.location.search + hash, { replace: true });
        return;
      }

      // If we have an explicit redirect parameter, honor it
      if (pathParams) {
        navigate(pathParams, { replace: true });
        return;
      }

      // If the hash has a type=signup, and no explicit redirect, we can assume it's a new user verification
      // The user specifically requested email verifications to always go to /checkout
      if (hash.includes('type=signup')) {
        navigate('/checkout', { replace: true });
        return;
      }
    } else if (hash && (hash.includes('error=') || hash.includes('error_description='))) {
      // Parse the hash parameters to extract the error description
      const hashParams = new URLSearchParams(hash.substring(1));
      const errorDesc = hashParams.get('error_description') || hashParams.get('error');

      // Navigate back to the signin page, passing the error and explicit redirect path
      let errorRedirectUrl = `/signin?error=${encodeURIComponent(errorDesc || 'Email link is invalid or has expired')}`;
      if (pathParams) {
        errorRedirectUrl += `&redirect=${encodeURIComponent(pathParams)}`;
      }
      navigate(errorRedirectUrl, { replace: true });
      return;
    }
  }, [searchParams, navigate]);

  const handleJoinSession = (code) => {
    navigate(`/listener?code=${code}`)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const handleJoinChurch = () => {
    navigate('/join-church')
  }

  const handleCreateChurch = () => {
    navigate('/translate/checkout')
  }

  const handleChurchJoinSuccess = () => {
    navigate('/')
  }

  // Redirect stream subdomain immediately to the livestream dashboard
  if (window.location.hostname === 'stream.exbabel.com' || window.location.hostname.startsWith('stream.')) {
    return <Navigate to="/dashboard/livestream" replace />
  }

  // Handle demo subdomain root
  if (window.location.hostname === 'demo.exbabel.com' || window.location.hostname.startsWith('demo.')) {
    return <Navigate to="/dashboard/demos" replace />
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
        <ExbabelLoading label="Loading account" />
      </div>
    )
  }

  if (error === 'network_error' || error === 'api_error') {
    return (
      <div className="min-h-screen flex flex-col gap-4 items-center justify-center p-6 text-center">
        <p>We could not load your account. Check your connection and try again.</p>
        <button className="rounded bg-blue-600 px-5 py-2 text-white" onClick={() => window.location.reload()}>Try again</button>
      </div>
    )
  }

  // 1. Visitor whose paid checkout is waiting for webhook -> finalize at /billing
  const pendingCheckoutUrl = isVisitor ? getPendingCheckoutUrl() : null
  if (pendingCheckoutUrl) {
    return <Navigate to={pendingCheckoutUrl} replace />
  }

  // 2. Visitor who was in the middle of checkout/signup -> resume their checkout
  const activeCheckoutUrl = isVisitor ? getActiveCheckoutUrl() : null
  if (activeCheckoutUrl) {
    return <Navigate to={activeCheckoutUrl} replace />
  }

  // Admin -> smart routing based on owned products
  if (isAdmin) {
    const hasTranslate = Boolean(access?.translate);
    const hasLive = Boolean(access?.live);
    const hasEvents = Boolean(access?.events);

    // Single product Exbabel Live users route directly to livestream dashboard without Translate onboarding
    if (hasLive && !hasTranslate && !hasEvents) {
      return <Navigate to="/dashboard/livestream" replace />
    }

    if (profile && profile.onboarding_completed === false) {
      return <Navigate to="/onboarding" replace />
    }

    // Smart App Routing: Workspace is strictly valid only when user has multiple (2+) subscriptions/apps.
    // If user only owns 1 product, route directly to that app without showing the workspace.
    const activeProductCount = [hasTranslate, hasLive, hasEvents].filter(Boolean).length;
    
    if (activeProductCount === 1) {
      if (hasTranslate) return <Navigate to="/translate/home" replace />
      if (hasLive) return <Navigate to="/dashboard/livestream" replace />
      if (hasEvents) return <Navigate to="/events" replace />
    }

    // 0 products: route to checkout
    if (activeProductCount === 0) {
      return <Navigate to="/translate/checkout" replace />
    }

    // 2+ products: go to Hub to select
    return <Navigate to="/workspace" replace />
  }

  // Member -> show member home
  if (isMember) {
    return (
      <MemberHome
        onSoloMode={() => navigate('/solo')}
        onJoinSession={handleJoinSession}
        onSignOut={handleSignOut}
        onJoinChurch={handleJoinChurch}
      />
    )
  }

  // Authenticated visitor (has account but no church)
  if (isVisitor) {
    // If they explicitly chose to leave /join-church (clicked Back or Logo), let them see VisitorHome!
    if (location.state?.leftJoinChurch) {
      return (
        <VisitorHome
          onJoinSession={handleJoinSession}
          onJoinChurch={handleJoinChurch}
          onCreateChurch={handleCreateChurch}
          onSignIn={() => navigate('/signin')}
          onSignUp={() => navigate('/signup')}
          onSignOut={handleSignOut}
        />
      )
    }
    return <Navigate to="/join-church" replace />
  }

  // Anonymous user -> show visitor home
  return (
    <VisitorHome
      onJoinSession={handleJoinSession}
      onJoinChurch={handleJoinChurch}
      onCreateChurch={handleCreateChurch}
      onSignIn={() => navigate('/signin')}
      onSignUp={() => navigate('/signup')}
      onSignOut={handleSignOut}
    />
  )
}

// Login page wrapper
function LoginRoute() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, loading } = useAuth()
  const redirectTo = searchParams.get('redirect') || '/'

  // Redirect authenticated users to their destination (handles OAuth callback)
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(redirectTo)
    }
  }, [isAuthenticated, loading, navigate, redirectTo])

  return (
    <LoginPage
      onSuccess={() => navigate(redirectTo)}
      onBack={() => navigate('/')}
      onSwitchToSignUp={() => navigate(`/signup${redirectTo !== '/' ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`)}
      redirectAfter={redirectTo !== '/' ? redirectTo : undefined}
    />
  )
}

// Sign up page wrapper
function SignUpRoute() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, loading } = useAuth()
  const redirectTo = searchParams.get('redirect') || '/'

  // Redirect authenticated users to their destination (handles email verification callback)
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(redirectTo)
    }
  }, [isAuthenticated, loading, navigate, redirectTo])

  return (
    <SignUpPage
      onSuccess={() => navigate(redirectTo)}
      onBack={() => navigate('/')}
      onSwitchToSignIn={() => navigate(`/signin${redirectTo !== '/' ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`)}
      redirectAfter={redirectTo !== '/' ? redirectTo : undefined}
    />
  )
}

// Listener page wrapper with QR code support
function ListenerRoute() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionCode = (searchParams.get('code') || searchParams.get('join') || '').toUpperCase()

  return <ListenerPage sessionCodeProp={sessionCode} onBackToHome={() => navigate('/')} />
}

// Solo page wrapper
function SoloRoute() {
  const navigate = useNavigate()
  return <SoloPage onBackToHome={() => navigate('/')} />
}

// Host page wrapper
function HostRoute() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const eventId = searchParams.get('eventId')
  const sessionId = searchParams.get('sessionId')
  const isEventHost = searchParams.get('mode') === 'events'
  const backDestination = isEventHost
    ? eventHostBackDestination(eventId, sessionId)
    : '/workspace'
  if (isEventHost && searchParams.get('session') && !searchParams.get('runtime')) {
    return <EventHostLaunch sessionId={searchParams.get('session')} eventId={eventId} />
  }
  return <HostPage onBackToHome={() => navigate(backDestination)} />
}

function EventHostLaunch({ sessionId, eventId }) {
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()
  const [error, setError] = useState('')
  const launchStarted = useRef(false)
  useEffect(() => {
    if (launchStarted.current) return
    launchStarted.current = true
    const launch = async () => {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(sessionId)) {
        setError('Invalid session link')
        return
      }
      try {
        const apiUrl = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || '')
        const response = await fetch(`${apiUrl}/api/v2/runtime/launch`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${await getAccessToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, product: 'events' }),
        })
        const data = await response.json()
        if (!response.ok) throw new Error(data.error === 'RUNTIME_ACTIVE' ? 'This session is already hosted. Open the event room to take over.' : data.error || 'Could not launch Host Mode')
        navigate(eventHostUrl(data.runtimeId, eventId, sessionId), { replace: true })
      } catch (err) {
        setError(err.message)
      }
    }
    launch()
  }, [sessionId, eventId, getAccessToken, navigate])
  return <div className="min-h-screen flex flex-col items-center justify-center gap-4">
    <p>{error || 'Opening Host Mode...'}</p>
    {error && <button onClick={() => navigate(eventHostBackDestination(eventId, sessionId))}>Back to Events</button>}
  </div>
}

function HostAccessRoute() {
  const [searchParams] = useSearchParams()
  if (searchParams.has('operatorToken')) return <HostRoute />
  return <ProtectedRoute requireAuth={true} requireAdmin={searchParams.get('mode') !== 'events'}><HostRoute /></ProtectedRoute>
}

// Demo page wrapper
function DemoRoute() {
  const navigate = useNavigate()
  return <DemoPage onBackToHome={() => navigate('/')} />
}

// Join church page wrapper
function JoinChurchRoute() {
  const navigate = useNavigate()

  const handleBack = () => {
    // If user was in the middle of checkout, return them to their checkout
    const activeCheckout = getActiveCheckoutUrl()
    if (activeCheckout) {
      navigate(activeCheckout)
      return
    }
    // Otherwise return to home with leftJoinChurch state so HomeRoute lets them see VisitorHome
    navigate('/', { state: { leftJoinChurch: true } })
  }

  return (
    <JoinChurchPage
      onBack={handleBack}
      onSuccess={() => navigate('/')}
      onSignIn={() => navigate('/signin?redirect=/join-church')}
    />
  )
}

// Create church page wrapper
function CreateChurchRoute() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signOut } = useAuth()
  const redirectTo = searchParams.get('redirect') || '/'

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  return (
    <CreateChurchPage
      onBack={handleSignOut}
      onSuccess={() => navigate(redirectTo)}
    />
  )
}

// Onboarding page wrapper
function OnboardingRoute() {
  const { isAdmin } = useAuth()
  if (!isAdmin) {
    return <Navigate to="/" replace />
  }
  return <OnboardingPage />
}

// Livestream player page wrapper
function LivestreamPlayerRoute() {
  const navigate = useNavigate()
  return <LivestreamPlayerPage onBackToHome={() => navigate('/')} />
}

// Livestream dashboard wrapper
function LivestreamDashboardRoute() {
  const { access, loading } = useAuth()
  if (!loading && !access?.live) {
    return <Navigate to="/live/checkout" replace />
  }
  return <LivestreamDashboard />
}

function LivestreamAnalyticsRoute() {
  const { access, loading } = useAuth()
  if (!loading && !access?.live) {
    return <Navigate to="/live/checkout" replace />
  }
  return <LivestreamAnalytics />
}

// Workspace Hub wrapper - strictly valid only when multiple (2+) subscriptions are purchased
function WorkspaceHubRoute() {
  const { signOut, access, loading } = useAuth()
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
        <ExbabelLoading label="Loading workspace" />
      </div>
    )
  }

  const hasTranslate = Boolean(access?.translate);
  const hasLive = Boolean(access?.live);
  const hasEvents = Boolean(access?.events);
  const activeProductCount = [hasTranslate, hasLive, hasEvents].filter(Boolean).length;

  // Workspace Hub is only valid when user has multiple subscriptions.
  // If user owns only 1 product, they do not see the workspace and route directly to their app:
  if (activeProductCount < 2) {
    if (activeProductCount === 1) {
      if (hasTranslate) return <Navigate to="/translate/home" replace />
      if (hasLive) return <Navigate to="/dashboard/livestream" replace />
      if (hasEvents) return <Navigate to="/events" replace />
    }
    return <Navigate to="/translate/checkout" replace />
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }
  
  return <WorkspaceHub onSignOut={handleSignOut} />
}

// Translate Home wrapper (formerly AdminHome)
function TranslateHomeRoute() {
  const navigate = useNavigate()
  const { signOut, access, loading } = useAuth()
  
  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const handleJoinSession = (code) => {
    navigate(`/listener?code=${code}`)
  }

  if (!loading && !access?.translate) {
    return <Navigate to="/translate/checkout" replace />
  }

  return (
    <AdminHome
      onHostSession={() => navigate('/host')}
      onSoloMode={() => navigate('/solo')}
      onJoinSession={handleJoinSession}
      onSignOut={handleSignOut}
    />
  )
}

// Redirect component for legacy checkout to preserve query params
function CheckoutRedirect() {
  const [searchParams] = useSearchParams()
  return <Navigate to={`/translate/checkout?${searchParams.toString()}`} replace />
}

// Intelligence Hub wrappers
function IntelligenceHubHomeRoute() {
  return <IntelligenceHubHome />
}

function SermonIntelligenceDetailRoute() {
  return <SermonIntelligenceDetail />
}

function PublicSermonIntelligenceDetailRoute() {
  return <PublicSermonIntelligenceDetail />
}

function ListenerSermonLibraryRoute() {
  return <ListenerSermonLibrary />
}

function EmployeeAuthRoute() {
  return <EmployeeAuth />
}

function EmployeePortalRoute() {
  return <EmployeePortal />
}

function AdminEmployeeManagementRoute() {
  return <AdminEmployeeManagement />
}

// Exbabel Events 2.0 route wrappers
function EventsDashboardRoute() {
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const handleSignOut = async () => { await signOut(); navigate('/') }
  return <EventsDashboard onSignOut={handleSignOut} />
}

function EventDetailRoute() {
  return <EventDetail />
}

const TeamManagement = lazy(() => import('./components/events/TeamManagementV2').then(m => ({ default: m.TeamManagement })))
const PurityLayout = lazy(() => import('./components/events/PurityLayout').then(m => ({ default: m.PurityLayout })))

function TeamDirectoryRoute() {
  return (
    <PurityLayout brandText="Team Directory">
      <TeamManagement />
    </PurityLayout>
  )
}

function AppContent() {
  // Global Anti-Loop Watchdog
  // Ensures that any audio element playing a live WebRTC stream (srcObject)
  // never has the loop attribute enabled, as this causes infinite echoing on iOS.
  useEffect(() => {
    const watchdog = setInterval(() => {
      const audioElements = document.querySelectorAll('audio, video');
      audioElements.forEach(el => {
        if (el.srcObject && el.loop) {
          console.warn('🛡️ [Anti-Loop Watchdog] Prevented audio loop! Forcefully disabling loop on live stream.');
          el.loop = false;
          el.removeAttribute('loop');
        }
      });
    }, 1000);
    return () => clearInterval(watchdog);
  }, []);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomeRoute />} />
      <Route path="/signin" element={<LoginRoute />} />
      <Route path="/signup" element={<SignUpRoute />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/set-password" element={<ResetPasswordPage />} />
      <Route path="/listener" element={<ListenerRoute />} />
      <Route path="/church/:slug/live" element={<LivestreamPlayerRoute />} />
      <Route path="/demo" element={<DemoRoute />} />
      <Route path="/salesdemo" element={<SalesDemoPage />} />
      <Route path="/join-church" element={<JoinChurchRoute />} />
      <Route path="/checkout" element={<CheckoutRedirect />} />
      <Route path="/direct-checkout" element={<TranslateCheckout noTrial />} />
      <Route path="/missionary/invite/:token" element={<MissionaryOnboarding />} />
      <Route path="/dashboard/missionaries" element={<ProtectedRoute requireAuth={true}><MissionaryTracking /></ProtectedRoute>} />
      <Route path="/translate/checkout" element={<TranslateCheckout />} />
      <Route path="/live/checkout" element={<CustomLiveSignup />} />
      <Route path="/events/checkout" element={<EventsCheckout />} />
      <Route path="/live/custom-deal" element={<CustomLiveSignup />} />
      <Route path="/voices" element={<VoicesPage />} />
      <Route path="/d/:token" element={<PersonalizedDemoPage />} />
      <Route path="/demos/:token" element={<PersonalizedDemoPage />} />
      <Route path="/admin/demos" element={<Navigate to="/dashboard/demos" replace />} />
      <Route path="/dashboard/demos" element={<AdminDemosPage />} />

      {/* Protected routes */}
      <Route
        path="/solo"
        element={
          <ProtectedRoute requireAuth={true} requireMember={true}>
            <SoloRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/host"
        element={<HostAccessRoute />}
      />
      <Route
        path="/create-church"
        element={
          <ProtectedRoute requireAuth={true}>
            <CreateChurchRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireAuth={true}>
            <OnboardingRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/billing"
        element={
          <ProtectedRoute requireAuth={true}>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/workspace"
        element={
          <ProtectedRoute requireAuth={true} requireAdmin={true}>
            <WorkspaceHubRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/translate/home"
        element={
          <ProtectedRoute requireAuth={true} requireAdmin={true}>
            <TranslateHomeRoute />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard/livestream"
        element={
          <ProtectedRoute requireAuth={true} requireAdmin={true}>
            <LivestreamDashboardRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/livestream/analytics"
        element={
          <ProtectedRoute requireAuth={true} requireAdmin={true}>
            <LivestreamAnalyticsRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute requireAuth={true} requireAdmin={true}>
            <GlobalSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/intelligence"
        element={
          <ProtectedRoute requireAuth={true} requireAdmin={true}>
            <IntelligenceHubHomeRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/intelligence/:sermonId"
        element={
          <ProtectedRoute requireAuth={true} requireAdmin={true}>
            <SermonIntelligenceDetailRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/sermons"
        element={
          <ProtectedRoute requireAuth={true}>
            <ListenerSermonLibraryRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/public/sermons/:sermonId"
        element={<PublicSermonIntelligenceDetailRoute />}
      />

      <Route
        path="/employeeportal/login"
        element={<EmployeeAuthRoute />}
      />

      <Route
        path="/employeeportal"
        element={
          <ProtectedEmployeeRoute>
            <EmployeePortalRoute />
          </ProtectedEmployeeRoute>
        }
      />
      <Route path="/employee-portal" element={<Navigate to="/employeeportal" replace />} />
      <Route path="/employee-portal/login" element={<Navigate to="/employeeportal/login" replace />} />

      <Route path="/admin/employees" element={<Navigate to="/dashboard/employees" replace />} />
      <Route
        path="/dashboard/employees"
        element={
          <ProtectedEmployeeRoute>
            <AdminEmployeeManagementRoute />
          </ProtectedEmployeeRoute>
        }
      />

      {/* Exbabel Events 2.0 Routes */}
      <Route
        path="/events"
        element={
          <ProtectedRoute requireAuth={true}>
            <PurityEventsWrapper>
              <OrgProvider>
                <EventsDashboardRoute />
              </OrgProvider>
            </PurityEventsWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/my-events"
        element={
          <ProtectedRoute requireAuth={true}>
            <PurityEventsWrapper>
              <OrgProvider>
                <MyEvents />
              </OrgProvider>
            </PurityEventsWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/team"
        element={
          <ProtectedRoute requireAuth={true}>
            <PurityEventsWrapper>
              <OrgProvider>
                <TeamDirectoryRoute />
              </OrgProvider>
            </PurityEventsWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/analytics"
        element={
          <ProtectedRoute requireAuth={true}>
            <PurityEventsWrapper>
              <OrgProvider>
                <EventsAnalyticsPage />
              </OrgProvider>
            </PurityEventsWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/integrations"
        element={
          <ProtectedRoute requireAuth={true}>
            <PurityEventsWrapper>
              <OrgProvider>
                <IntegrationsPage />
              </OrgProvider>
            </PurityEventsWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/zoom-app"
        element={
          <ProtectedRoute requireAuth={true}>
            <OrgProvider><ZoomInterpretationApp /></OrgProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <ProtectedRoute requireAuth={true}>
            <PurityEventsWrapper>
              <OrgProvider>
                <EventDetail />
              </OrgProvider>
            </PurityEventsWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId/sessions/:sessionId"
        element={
          <ProtectedRoute requireAuth={true}>
            <PurityEventsWrapper>
              <OrgProvider>
                <SessionDetail />
              </OrgProvider>
            </PurityEventsWrapper>
          </ProtectedRoute>
        }
      />
      {/* Operator Link Entry Point */}
      <Route path="/events/billing" element={<ProtectedRoute requireAuth={true}><OrgProvider><EventsBilling /></OrgProvider></ProtectedRoute>} />
      <Route path="/operator/:token" element={<OperatorLinkEntry />} />

      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]"><ExbabelLoading label="Loading Exbabel" /></div>}>
          <AppContent />
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
